import { Box } from '@hub/box'
import { Grid } from '@hub/grid'
import { Link } from '@hub/link'
import { Stack } from '@hub/stack'
import { Text } from '@hub/text'
import {
  CloudinaryImage,
  IMAGE_SIZES_BROWSE_GRID,
} from '@scentregroup/shared/hub-components/cloudinary-image'
import { ViewType } from '@scentregroup/shared/hub-components/view-control'
import React from 'react'
import { trackClickNationalStoreDirectoryItem } from './analytics'
import { INationalStoreDirectoryItem } from './types'
import { H5 } from '@hub/heading'

const ABOVE_FOLD_THRESHOLD = 8

type NationalStoreDirectoryProps = {
  items: INationalStoreDirectoryItem[]
  view?: ViewType
}

export const NationalStoreDirectory: React.FC<NationalStoreDirectoryProps> = ({
  items,
  view,
}) => (
  <Box sx={{ marginBottom: 'spacing-lg' }}>
    <Grid
      templateColumns={
        view === 'list'
          ? '1fr'
          : ['repeat(2, 1fr)', null, 'repeat(4, 1fr)', null, 'repeat(5, 1fr)']
      }
      columnGap={['spacing-sm', null, 'spacing-md']}
      rowGap={['spacing-md', null, 'spacing-lg']}
    >
      {items.map((item, index) => (
        <Stack
          key={item.id}
          shouldWrapChildren={false}
          gap="spacing-md"
          direction={view === 'list' ? 'row' : 'column'}
        >
          <Link
            href={item.storefrontUrl}
            sx={{
              aspectRatio: 1,
              width: view === 'list' ? 'size-md' : 'auto',
              minWidth: '120px',
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: item.imageUrl ? 'ghost' : 'surfaceBrandSecondary',
              ...(item.imageUrl
                ? null
                : { backgroundColor: 'surfaceBrandPrimary' }),
            }}
            onClick={trackClickNationalStoreDirectoryItem(item)}
          >
            {item.imageUrl ? (
              <CloudinaryImage
                ratio={1}
                resizeMode="pad"
                imageSetOrImage={{
                  url: item.imageUrl,
                  type: 'SQUARE',
                }}
                sizes={IMAGE_SIZES_BROWSE_GRID}
                loadingMode={index < ABOVE_FOLD_THRESHOLD ? 'eager' : 'lazy'}
              />
            ) : (
              <Box
                sx={{
                  padding: 'spacing-sm',
                  fontSize: ['font-sm', null, 'font-xl'],
                  textAlign: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                {item.title}
              </Box>
            )}
          </Link>
          <Box
            sx={{
              width:
                view === 'list'
                  ? '100%'
                  : view === 'grid'
                  ? 'auto'
                  : ['100%', null, 'auto'],
            }}
          >
            <H5>{item.title}</H5>
            {item.retailCategories.length > 0 && (
              <Box
                sx={{
                  marginTop: 'spacing-xs',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  ['-webkit-line-clamp']: '3',
                  ['-webkit-box-orient']: 'vertical',
                }}
              >
                <Text isBodyText={true} color={'textSecondary'}>
                  {item.retailCategories.join(' | ')}
                </Text>
              </Box>
            )}
            <Box sx={{ marginTop: ['spacing-sm', undefined, 'spacing-md'] }}>
              <Link
                hasUnderline={true}
                href={item.storefrontUrl}
                onClick={trackClickNationalStoreDirectoryItem(item)}
              >
                View all store locations
              </Link>
            </Box>
          </Box>
        </Stack>
      ))}
    </Grid>
  </Box>
)
